import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Map raw payment error messages to user-friendly text.
 * Keeps technical details in console logs for debugging.
 * 
 * @param {string} rawError - Raw error message from backend/Finix
 * @returns {string} - User-friendly error message
 */
export function getFriendlyPaymentError(rawError) {
  if (!rawError || typeof rawError !== 'string') {
    return "We couldn't process your payment method. Please try again.";
  }
  
  const lowerError = rawError.toLowerCase();
  
  // Card validation errors
  if (lowerError.includes('declined') || lowerError.includes('do_not_honor')) {
    return "Your card was declined. Please try a different payment method.";
  }
  if (lowerError.includes('invalid card') || lowerError.includes('card number')) {
    return "That card could not be verified. Please check the card number and try again.";
  }
  if (lowerError.includes('cvv') || lowerError.includes('cvc') || lowerError.includes('security code')) {
    return "The security code appears to be invalid. Please check and try again.";
  }
  if (lowerError.includes('expir')) {
    return "The expiration date appears to be invalid. Please check and try again.";
  }
  if (lowerError.includes('insufficient funds')) {
    return "The card has insufficient funds. Please try a different payment method.";
  }
  if (lowerError.includes('lost') || lowerError.includes('stolen')) {
    return "This card cannot be used. Please try a different payment method.";
  }
  if (lowerError.includes('fraud') || lowerError.includes('restricted')) {
    return "This card cannot be used. Please try a different payment method.";
  }
  
  // Network/service errors
  if (lowerError.includes('timeout') || lowerError.includes('timed out')) {
    return "The payment service is taking too long. Please try again.";
  }
  if (lowerError.includes('network') || lowerError.includes('connection')) {
    return "Connection error. Please check your internet and try again.";
  }
  if (lowerError.includes('unavailable') || lowerError.includes('service')) {
    return "Payment service is temporarily unavailable. Please try again in a moment.";
  }
  
  // Response parsing errors - make these friendly
  if (lowerError.includes('body stream') || lowerError.includes('already read')) {
    return "Unable to process payment response. Please try again.";
  }
  if (lowerError.includes('failed to read') || lowerError.includes('parse')) {
    return "Unable to process payment response. Please try again.";
  }
  if (lowerError.includes('[object object]')) {
    return "An error occurred processing your payment. Please try again.";
  }
  
  // Generic cleanup - remove technical jargon
  let cleaned = rawError
    .replace(/finix/gi, 'payment')
    .replace(/\[object Object\]/g, 'An error occurred')
    .replace(/HTTP \d{3}:?\s*/gi, '')
    .replace(/Error \(\d+\):?\s*/gi, '');
  
  // If still too technical, use generic message
  if (cleaned.includes('_') || cleaned.includes('::') || cleaned.length > 150) {
    return "We couldn't add that payment method. Please review the card details and try again.";
  }
  
  return cleaned || "We couldn't process your payment method. Please try again.";
}

/**
 * Safely parse a fetch Response - reads body ONCE only.
 * Prevents "body stream already read" errors.
 * 
 * @param {Response} response - fetch Response object
 * @returns {Promise<{ok: boolean, status: number, data: object|null, text: string|null, error: string|null}>}
 */
export async function parseResponseSafely(response) {
  const result = {
    ok: response.ok,
    status: response.status,
    data: null,
    text: null,
    error: null,
  };
  
  try {
    // Read the body as text ONCE
    const textBody = await response.text();
    result.text = textBody;
    
    // Try to parse as JSON if content looks like JSON
    if (textBody && textBody.trim().startsWith('{')) {
      try {
        result.data = JSON.parse(textBody);
      } catch (jsonErr) {
        // Not valid JSON - keep text only
        result.error = `Invalid JSON response: ${textBody.substring(0, 100)}`;
      }
    } else if (textBody && textBody.trim().startsWith('[')) {
      try {
        result.data = JSON.parse(textBody);
      } catch (jsonErr) {
        result.error = `Invalid JSON response: ${textBody.substring(0, 100)}`;
      }
    } else if (textBody) {
      // Plain text response
      result.error = textBody.substring(0, 200);
    }
  } catch (readErr) {
    // Log technical details for debugging but show friendly message to users
    console.error('[parseResponseSafely] Response read error:', readErr.message);
    result.error = "Unable to process payment response. Please try again.";
  }
  
  return result;
}
